import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ApiException,
  apiPost,
  companyIdSelector,
  errors,
  getBulkPassengersStationMapDataPath,
  IApiError,
  IGetPassengersMapStationInfoPayload,
  IPassengerStationsMapInfo,
  Log,
  normalizeErrors,
  PASSENGERS_MAP_DEFAULT_FILTER_TYPE,
  passengersMapAddStationSearchValueAtom,
  passengersMapCurrentPolylineViewAtom,
  passengersMapSelectedControlBtnAtom,
  passengersMapSelectedStationDictionaryAtom,
  passengersStationsMapDataAtom,
  passengersStationsMapLastFilterSearchAtom,
  SimpleResult,
  toForbiddenTranslatableItem,
  translateInfoItem,
} from "@ryde/shared";

import { useSelectedIds } from "../use-selected-ids";

export const usePassengersMapBulkActions = () => {
  const { t } = useTranslation();
  const setApiFetching = useSetRecoilState(apiDataFetchingAtom);
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const companyId = useRecoilValue(companyIdSelector);
  const { selectedRowsIds } = useSelectedIds();
  const [
    passengersStationsMapLastFilterSearch,
    setPassengersStationsMapLastFilterSearch,
  ] = useRecoilState(passengersStationsMapLastFilterSearchAtom);

  const setPassengersMapCurrentPolylineViewAtom = useSetRecoilState(
    passengersMapCurrentPolylineViewAtom,
  );
  const setPassengersStationsMapDataAtom = useSetRecoilState(
    passengersStationsMapDataAtom,
  );
  const setPassengersMapSelectedStationDictionary = useSetRecoilState(
    passengersMapSelectedStationDictionaryAtom,
  );
  const setPassengersMapAddStationSearchValueAtom = useSetRecoilState(
    passengersMapAddStationSearchValueAtom,
  );
  const setPassengersMapSelectedControlBtnAtom = useSetRecoilState(
    passengersMapSelectedControlBtnAtom,
  );

  const clearAllData = useEvent(() => {
    setPassengersMapCurrentPolylineViewAtom([]);
    setPassengersMapAddStationSearchValueAtom(undefined);
    setPassengersMapSelectedControlBtnAtom(undefined);
    setPassengersMapSelectedStationDictionary(new Map());
  });

  const bulkFetchPassengersStationMapData = useEvent(
    async (payload: IGetPassengersMapStationInfoPayload): Promise<boolean> => {
      clearAllData();
      setPassengersStationsMapLastFilterSearch(payload);

      try {
        setErrors(undefined);
        setApiFetching(true);
        const requestUrl = getBulkPassengersStationMapDataPath(companyId || 0);
        const response = await apiPost<
          SimpleResult<IPassengerStationsMapInfo[]> | undefined
        >(requestUrl, {
          body: payload,
        });

        const { success, data } = response ?? {};

        if (!success) {
          Log.errorAndToast(t("errors.failedToFetchPassengersMapStation"));
          return false;
        }

        if (Array.isArray(data)) {
          setPassengersStationsMapDataAtom(data);
        }

        return true;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.failedToFetchPassengersMapStation"),
            errorDetails,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return false;
        }

        Log.errorAndToast(t("errors.failedToFetchPassengersMapStation"), error);
        setErrors(normalizeErrors(error as IApiError, t));
        return false;
      } finally {
        setApiFetching(false);
      }
    },
  );

  const bulkRefetchPassengersStationMapData = useEvent(
    async (): Promise<boolean> => {
      const payload: IGetPassengersMapStationInfoPayload = {
        filterType:
          passengersStationsMapLastFilterSearch?.filterType ||
          PASSENGERS_MAP_DEFAULT_FILTER_TYPE,
        selectedPassengersIds:
          passengersStationsMapLastFilterSearch?.selectedPassengersIds ||
          selectedRowsIds,
      };

      return bulkFetchPassengersStationMapData(payload);
    },
  );

  return {
    bulkFetchPassengersStationMapData,
    bulkRefetchPassengersStationMapData,
  };
};
